import { useEffect, useId, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { Search, X } from "lucide-react";
import type { ApiPlayable, SearchResult } from "@hive/sdk";
import { observer } from "mobx-react-lite";
import { useStore } from "../../stores/rootStore";
import { hive } from "../../lib/apiClient";
import { track } from "../../lib/analytics";
import { authorLabel, coverGradient, playableName } from "../../lib/playable";
import { playableKind, playablePath } from "../../lib/playableKind";
import { playableCoverImageProps } from "../../lib/playableCoverImage";
import { Avatar } from "../ui/Avatar";
import { Spinner } from "../ui/Spinner";

/**
 * Header type-ahead: typing searches in place and the matches float under the
 * input, so looking something up never takes the visitor off the page they are
 * on. Escape, the clear button, or a click outside dismisses the panel and
 * leaves the page exactly as it was.
 */
export const HeaderSearch = observer(function HeaderSearch() {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { auth, ui } = useStore();
	const viewerId = auth.user?.id ?? null;
	const [query, setQuery] = useState("");
	const [result, setResult] = useState<SearchResult | null>(null);
	const [loading, setLoading] = useState(false);
	const [open, setOpen] = useState(false);
	const [expanded, setExpanded] = useState(false);
	const [failed, setFailed] = useState(false);
	const [retry, setRetry] = useState(0);
	const inputId = useId();
	const rootRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	// Results render as they arrive, so a slow early response must not overwrite
	// a newer one: only the latest request id is allowed to publish.
	const requestId = useRef(0);

	useEffect(() => {
		if (expanded) inputRef.current?.focus();
	}, [expanded]);

	useEffect(() => {
		const q = query.trim();
		setResult(null);
		setFailed(false);
		if (!q || !viewerId) {
			requestId.current++;
			setResult(null);
			setLoading(false);
			return;
		}
		setLoading(true);
		const id = ++requestId.current;
		const timer = setTimeout(async () => {
			try {
				const res = await hive.search.search(q, { limit: 8 });
				if (id === requestId.current) setResult(res);
			} catch {
				if (id === requestId.current) setFailed(true);
			} finally {
				if (id === requestId.current) setLoading(false);
			}
		}, 320);
		return () => { clearTimeout(timer); requestId.current++; };
	}, [query, viewerId, retry]);

	useEffect(() => {
		if (!open && !expanded) return;

		function handlePointerDown(event: PointerEvent) {
			if (!rootRef.current?.contains(event.target as Node)) {
				setOpen(false);
				setExpanded(false);
			}
		}

		function handleKeyDown(event: KeyboardEvent) {
			if (event.key === "Escape") {
				setOpen(false);
				setExpanded(false);
				inputRef.current?.blur();
			}
		}

		document.addEventListener("pointerdown", handlePointerDown);
		document.addEventListener("keydown", handleKeyDown);
		return () => {
			document.removeEventListener("pointerdown", handlePointerDown);
			document.removeEventListener("keydown", handleKeyDown);
		};
	}, [open, expanded]);

	function clearQuery() {
		setQuery("");
		inputRef.current?.focus();
	}

	function openCreator(handle: string) {
		setOpen(false);
		setExpanded(false);
		navigate(`/u/${handle}`);
	}

	function openPlayable(item: ApiPlayable, position: number) {
		setOpen(false);
		setExpanded(false);
		track("content_opened", {
			content_id: item.id,
			content_kind: playableKind(item),
			source: "header_search",
			position,
		});
		navigate(playablePath(item));
	}

	const hasResults = !!result && (result.creators.length > 0 || result.playables.length > 0);
	const showPanel = open && query.trim().length > 0;

	return (
		<div ref={rootRef} className="global-search" role="search">
			<button
				type="button"
				className="global-search-trigger topbar-control"
				aria-label={t("layout.search")}
				aria-expanded={expanded}
				aria-controls={inputId}
				onClick={() => { setExpanded((value) => !value); setOpen(false); }}
			>
				<Search size={18} />
			</button>
			<div className="global-search-field" data-expanded={expanded}>
				<Search
					size={15}
					className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8d86a0]"
				/>
				<input
					id={inputId}
					ref={inputRef}
					value={query}
					onChange={(event) => {
						setQuery(event.target.value);
						setOpen(true);
					}}
					onFocus={() => setOpen(true)}
					aria-label={t("layout.search")}
					placeholder={t("search.placeholder")}
					autoComplete="off"
					className="h-9 w-full rounded-full border border-line bg-surface-2 pl-9 pr-9 text-sm text-text outline-none transition placeholder:text-muted hover:border-brand/50 focus:border-brand/60"
				/>
				{loading ? (
					<Spinner size={15} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-brand" />
				) : (
					query.length > 0 && (
						<button
							type="button"
							onClick={clearQuery}
							aria-label={t("search.clear")}
							className="absolute right-2.5 top-1/2 grid size-5 -translate-y-1/2 place-items-center rounded-full text-[#8d86a0] transition hover:bg-white/10 hover:text-white"
						>
							<X size={13} />
						</button>
					)
				)}
			</div>

			{showPanel && (
				// Below lg the input is only as wide as the gap between the logo and the
				// header buttons — far too narrow to read a title in. There the panel
				// detaches to the header's own width instead of the input's.
				<div className="global-search-results z-30 max-h-[min(70vh,420px)] overflow-y-auto overscroll-contain rounded-xl border border-line bg-surface p-2 shadow-panel">
					{!viewerId ? (
						<div className="space-y-3 px-2 py-5 text-center text-sm text-muted">
							<p>{t("search.signInToSearch")}</p>
							<button type="button" className="btn-primary" onClick={() => ui.openAuth()}>{t("common.signIn")}</button>
						</div>
					) : failed ? (
						<div role="alert" className="space-y-3 px-2 py-5 text-center text-sm text-muted">
							<p>{t("common.loadFailed")}</p>
							<button type="button" className="underline underline-offset-4" onClick={() => setRetry((value) => value + 1)}>{t("common.retry")}</button>
						</div>
					) : !hasResults ? (
						<p className="px-2 py-6 text-center text-sm text-muted">
							{loading ? t("common.loading") : t("search.noResults")}
						</p>
					) : (
						<div className="flex flex-col gap-3">
							{result && result.playables.length > 0 && (
								<section>
									<p className="px-2 pb-1 text-[11px] font-bold uppercase tracking-wide text-faint">
										{t("search.playables")}
									</p>
									{result.playables.map((item, index) => (
										<button
											key={item.id}
											type="button"
											onClick={() => openPlayable(item, index + 1)}
											className="flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left transition hover:bg-white/8"
										>
											{item.coverUrl ? (
												<img
													{...playableCoverImageProps(item.coverUrl, "32px", 320)}
													alt=""
													loading="lazy"
													className="size-8 shrink-0 rounded-md object-cover"
												/>
											) : (
												<span
													className="size-8 shrink-0 rounded-md"
													style={{ background: coverGradient(item) }}
												/>
											)}
											<span className="min-w-0">
												<span className="block truncate text-sm font-semibold text-text">
													{playableName(item)}
												</span>
												<span className="block truncate text-xs text-faint">
													{authorLabel(item)}
												</span>
											</span>
										</button>
									))}
								</section>
							)}

							{result && result.creators.length > 0 && (
								<section>
									<p className="px-2 pb-1 text-[11px] font-bold uppercase tracking-wide text-faint">
										{t("search.creators")}
									</p>
									{result.creators.map((creator) => (
										<button
											key={creator.id}
											type="button"
											onClick={() => openCreator(creator.handle)}
											className="flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left transition hover:bg-white/8"
										>
											<Avatar
												name={creator.displayName ?? creator.handle}
												src={creator.avatarUrl}
												size={32}
											/>
											<span className="min-w-0">
												<span className="block truncate text-sm font-semibold text-text">
													{creator.displayName || creator.handle}
												</span>
												<span className="block truncate text-xs text-faint">
													@{creator.handle}
												</span>
											</span>
										</button>
									))}
								</section>
							)}
						</div>
					)}
				</div>
			)}
		</div>
	);
});
